import type { CalendarDate } from '@internationalized/date';
import { getLocalTimeZone, today } from '@internationalized/date';
import { clsx } from 'clsx';
import { useMemo, useRef } from 'react';
import { mergeProps, useCalendarCell, useFocusRing } from 'react-aria';
import type { CalendarState } from 'react-stately';
import { Flex } from '../../../Flex/Flex';
import { Text } from '../../../Text/Text';
import { formatCalendarDate } from '../../utils';
import * as styles from './CalendarViews.css';

interface DayProps {
  readonly state: CalendarState;
  readonly date: CalendarDate;
}

export const Day: React.FC<DayProps> = ({ state, date }) => {
  const ref = useRef<HTMLDivElement | null>(null);
  const { focusProps } = useFocusRing();
  const { cellProps, buttonProps, isOutsideVisibleRange, isSelected, formattedDate } = useCalendarCell(
    { date },
    state,
    ref,
  );

  const isToday = useMemo(() => formatCalendarDate(today(getLocalTimeZone())) === formatCalendarDate(date), [date]);

  const isDayDisabled = useMemo(
    () => (state.maxValue != null && date > state.maxValue) || (state.minValue != null && date < state.minValue),
    [date, state.maxValue, state.minValue],
  );

  return (
    <td {...cellProps}>
      <Flex
        alignItems="center"
        as="button"
        className={clsx(styles.day, {
          [styles.selectedDate]: isSelected,
          [styles.outsideVisibleRange]: isOutsideVisibleRange,
          [styles.today]: isToday,
        })}
        cursor="pointer"
        disabled={isDayDisabled}
        justifyContent="center"
        m="0 auto"
        padding={styles.CELL_PADDING}
        ref={ref}
        type="button"
        width={styles.CELL_WIDTH}
        {...mergeProps(buttonProps, focusProps)}
      >
        <Text type="bodyMedium">{formattedDate}</Text>
      </Flex>
    </td>
  );
};
